/**
 * Time Series Processor for aggregating WebSocket messages into time intervals
 * Used for generating time-series data for charts and visualizations
 */

import { formatChartTime } from '../utils/timeUtils';

class TimeSeriesProcessor {
  constructor() {
    this.intervalMs = 10000; // 10 seconds
    this.timeSeriesData = [];
    this.sourceColors = {};
    this.lastProcessedTime = 0;
    this.knownSources = new Map(); // Track all sources we've seen: sourceId -> sourceName
  }

  /**
   * Process a batch of messages and aggregate them into time intervals
   * @param {Array} messages - Array of message objects with timestamp and sourceId
   * @param {number} currentTime - Current timestamp (ms since epoch)
   * @returns {Array} - Updated time series data
   */
  processMessages(messages, currentTime = Date.now()) {
    // MonitoringDataProcessor already passes only newly ingested messages by
    // buffer sequence. Do not filter by event timestamp here.
    const newMessages = Array.isArray(messages) ? messages : [];

    // Update known sources from new messages
    if (newMessages && newMessages.length > 0) {
      newMessages.forEach(message => {
        const sourceId = message.sourceId || 'unknown';
        const sourceName = message.sourceName || 'Unknown';
        this.knownSources.set(sourceId, sourceName);
      });
    }

    // Group NEW messages by their actual interval timestamps
    const newMessagesByInterval = {};

    if (newMessages && newMessages.length > 0) {
      newMessages.forEach(message => {
        const timestamp = message.timestamp;
        const sourceId = message.sourceId || 'unknown';

        // Validate timestamp
        if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
          console.warn('Invalid message timestamp:', message);
          return; // Skip this message
        }

        // Calculate interval (floor to nearest interval)
        const interval = Math.floor(timestamp / this.intervalMs) * this.intervalMs;

        // Validate calculated interval
        if (isNaN(interval) || interval <= 0) {
          console.warn('Invalid calculated interval for timestamp:', timestamp, interval);
          return; // Skip this message
        }

        // Initialize interval if not exists
        if (!newMessagesByInterval[interval]) {
          newMessagesByInterval[interval] = {};
        }

        // Initialize source if not exists
        if (!newMessagesByInterval[interval][sourceId]) {
          newMessagesByInterval[interval][sourceId] = 0;
        }

        // Increment count
        newMessagesByInterval[interval][sourceId]++;
      });
    }

    // Update time series with new messages and maintain history
    this._updateTimeSeriesWithNewMessages(newMessagesByInterval, currentTime);

    // Update last processed time
    this.lastProcessedTime = currentTime;

    return this.timeSeriesData;
  }

  /**
   * Update time series with new messages while maintaining history
   * @private
   * @param {Object} newMessagesByInterval - New messages grouped by interval
   * @param {number} currentTime - Current timestamp
   */
  _updateTimeSeriesWithNewMessages(newMessagesByInterval, currentTime) {
    const currentInterval = Math.floor(currentTime / this.intervalMs) * this.intervalMs;
    const windowSize = 60 * 60 * 1000; // 1 hour
    const windowStart = currentInterval - windowSize;

    // Create a map of existing intervals for quick lookup
    const existingIntervals = new Map();
    this.timeSeriesData.forEach(interval => {
      existingIntervals.set(interval.timestamp, interval);
    });

    // Update intervals with new messages
    Object.entries(newMessagesByInterval).forEach(([timestampStr, sourceCounts]) => {
      const timestamp = parseInt(timestampStr);

      if (existingIntervals.has(timestamp)) {
        // Update existing interval
        const interval = existingIntervals.get(timestamp);
        Object.entries(sourceCounts).forEach(([sourceId, count]) => {
          if (interval.sources[sourceId]) {
            interval.sources[sourceId].count += count;
          } else {
            const sourceName = this.knownSources.get(sourceId) || 'Unknown';
            interval.sources[sourceId] = { count, sourceName };
          }
        });
        // Recalculate total
        interval.total = Object.values(interval.sources).reduce((sum, source) => sum + source.count, 0);
      } else {
        // Create new interval
        const date = new Date(timestamp);
        const sources = {};

        // Initialize all known sources to 0
        this.knownSources.forEach((sourceName, sourceId) => {
          sources[sourceId] = {
            count: sourceCounts[sourceId] || 0,
            sourceName
          };
        });

        const total = Object.values(sources).reduce((sum, source) => sum + source.count, 0);

        existingIntervals.set(timestamp, {
          timestamp,
          date,
          sources,
          total
        });
      }
    });

    // Ensure current interval exists (even if no new messages)
    if (!existingIntervals.has(currentInterval) && this.knownSources.size > 0) {
      const sources = {};
      this.knownSources.forEach((sourceName, sourceId) => {
        sources[sourceId] = { count: 0, sourceName };
      });

      existingIntervals.set(currentInterval, {
        timestamp: currentInterval,
        date: new Date(currentInterval),
        sources,
        total: 0
      });
    }

    // Filter to window and sort
    this.timeSeriesData = Array.from(existingIntervals.values())
      .filter(interval => interval.timestamp >= windowStart)
      .sort((a, b) => a.timestamp - b.timestamp);


  }

  /**
   * Get time series data for a specific time window
   * @param {number} timeWindow - Time window in milliseconds (default: 5 minutes)
   * @returns {Array} - Filtered time series data
   */
  getTimeSeriesData(timeWindow = 5 * 60 * 1000) {
    // Filter the data to the requested time window
    const cutoff = this.lastProcessedTime - timeWindow;
    return this.timeSeriesData.filter(d => d.timestamp >= cutoff);
  }

  /**
   * Get all unique source IDs from the time series data
   * @returns {Array} - Array of source IDs
   */
  getSourceIds() {
    const sourceIds = new Set();

    this.timeSeriesData.forEach(interval => {
      Object.keys(interval.sources || {}).forEach(sourceId => {
        sourceIds.add(sourceId);
      });
    });

    return Array.from(sourceIds);
  }

  /**
   * Get source names mapped to their IDs
   * @returns {Object} - Map of source IDs to names
   */
  getSourceNames() {
    const sourceNames = {};

    this.timeSeriesData.forEach(interval => {
      Object.entries(interval.sources || {}).forEach(([sourceId, data]) => {
        if (data.sourceName) {
          sourceNames[sourceId] = data.sourceName;
        }
      });
    });

    return sourceNames;
  }

  /**
   * Format time series data for recharts library
   * @param {number} timeWindow - Time window in milliseconds
   * @returns {Array} - Formatted data for charts
   */
  getFormattedChartData(timeWindow = 5 * 60 * 1000) {
    const data = this.getTimeSeriesData(timeWindow);
    const sourceIds = this.getSourceIds();

    // Format for recharts
    return data.map(interval => {
      // Validate interval data
      if (!interval || typeof interval.timestamp !== 'number' || isNaN(interval.timestamp)) {
        console.warn('Invalid interval data:', interval);
        return null;
      }

      // Validate date object
      if (!interval.date || isNaN(interval.date.getTime())) {
        console.warn('Invalid date in interval:', interval);
        return null;
      }

      const result = {
        timestamp: interval.timestamp,
        time: formatChartTime(interval.timestamp),
        total: interval.total || 0
      };

      // Add count for each source (0 if not present)
      sourceIds.forEach(sourceId => {
        result[sourceId] = (interval.sources[sourceId]?.count || 0);
      });

      return result;
    }).filter(Boolean); // Remove null entries
  }

  /**
   * Clear all time series data
   */
  clearData() {
    this.timeSeriesData = [];
    this.lastProcessedTime = 0;
    this.knownSources.clear();

  }

  /**
   * Initialize for real-time processing (start from current time)
   */
  initializeRealTime() {
    this.lastProcessedTime = Date.now();

  }

  /**
   * Set the interval size for aggregation
   * @param {number} intervalMs - Interval size in milliseconds
   */
  setIntervalSize(intervalMs) {
    if (intervalMs > 0 && intervalMs !== this.intervalMs) {
      this.intervalMs = intervalMs;
      // Only clear data when actually changing interval size
      this.clearData();
      if (process.env.NODE_ENV === 'development') {
        console.log('TimeSeriesProcessor: Interval size changed to', intervalMs, 'ms, data cleared');
      }
    }
  }

  /**
   * Get debug information about the current state
   * @returns {Object} - Debug information
   */
  getDebugInfo() {
    return {
      intervalMs: this.intervalMs,
      totalIntervals: this.timeSeriesData.length,
      knownSources: Array.from(this.knownSources.keys()),
      lastProcessedTime: this.lastProcessedTime,
      timeRange: this.timeSeriesData.length > 0 ? {
        start: new Date(this.timeSeriesData[0].timestamp).toISOString(),
        end: new Date(this.timeSeriesData[this.timeSeriesData.length - 1].timestamp).toISOString()
      } : null
    };
  }

  /**
   * Test method to verify the real-time logic works correctly
   * Only available in development mode
   */
  runTest() {
    if (process.env.NODE_ENV !== 'development') {
      console.warn('Test method only available in development mode');
      return;
    }

    console.log('=== TimeSeriesProcessor Real-Time Test ===');

    // Simulate real-time processing
    const baseTime = Date.now();

    // First processing cycle - some initial messages
    console.log('\n--- Processing Cycle 1 (T+0s) ---');
    const messages1 = [
      { sourceId: 'source-a', sourceName: 'Source A', timestamp: baseTime - 5000 },
      { sourceId: 'source-a', sourceName: 'Source A', timestamp: baseTime - 4000 },
      { sourceId: 'source-b', sourceName: 'Source B', timestamp: baseTime - 3000 }
    ];
    this.processMessages(messages1, baseTime);
    this._logRecentIntervals();

    // Second processing cycle - 10 seconds later with new messages
    console.log('\n--- Processing Cycle 2 (T+10s) ---');
    const time2 = baseTime + 10000;
    const messages2 = [
      ...messages1, // Previous messages (should be ignored as already processed)
      { sourceId: 'source-a', sourceName: 'Source A', timestamp: time2 - 2000 },
      { sourceId: 'source-b', sourceName: 'Source B', timestamp: time2 - 1000 },
      { sourceId: 'source-b', sourceName: 'Source B', timestamp: time2 - 500 }
    ];
    this.processMessages(messages2, time2);
    this._logRecentIntervals();

    // Third processing cycle - 10 seconds later with no new messages
    console.log('\n--- Processing Cycle 3 (T+20s) - No new messages ---');
    const time3 = baseTime + 20000;
    this.processMessages(messages2, time3); // Same messages, no new ones
    this._logRecentIntervals();

    console.log('\nExpected behavior:');
    console.log('- Cycle 1: Should show counts for completed intervals with messages');
    console.log('- Cycle 2: Should show new completed interval with new message counts');
    console.log('- Cycle 3: Should show completed intervals with 0 counts for periods with no messages');
    console.log('- Each interval represents the final count for that completed 10-second window');
    console.log('- Current (incomplete) interval should not appear until it completes');
    console.log('=== End Test ===');
  }

  /**
   * Helper method to log recent intervals for testing
   * @private
   */
  _logRecentIntervals() {
    const recentIntervals = this.timeSeriesData.slice(-3);
    recentIntervals.forEach(interval => {
      console.log(`${new Date(interval.timestamp).toISOString()}: Total=${interval.total}`,
        Object.entries(interval.sources).map(([id, data]) => `${data.sourceName}=${data.count}`).join(', '));
    });
  }
}

// Create singleton instance
const timeSeriesProcessor = new TimeSeriesProcessor();

// Expose test method globally in development
if (process.env.NODE_ENV === 'development') {
  window.testTimeSeriesProcessor = () => timeSeriesProcessor.runTest();
  console.log('Development mode: Use window.testTimeSeriesProcessor() to run tests');
}

export default timeSeriesProcessor;
